import { COLOR_PRESETS } from '@/hooks/useStoreSettings';
import { LAYOUT_OPTIONS, HERO_STYLES } from './designConstants';
import { DesignSettings, SectionSaveState } from './designTypes';
import { SaveButton } from './DesignShared';

interface Props {
  design: DesignSettings;
  updateDesign: (patch: Partial<DesignSettings>) => void;
  layoutSaveState: SectionSaveState;
  onSaveLayout: () => void;
  selectedColor: typeof COLOR_PRESETS[0];
}

export default function LayoutSection({ design, updateDesign, layoutSaveState, onSaveLayout, selectedColor }: Props) {
  return (
    <div className="flex flex-col gap-5">
      <div className="bg-gray-50 rounded-2xl p-5 border border-gray-100">
        <h3 className="text-sm font-bold text-[#1a1a1a] mb-1 flex items-center gap-2">
          <div className="w-6 h-6 flex items-center justify-center bg-[#1a1a1a] rounded-lg">
            <i className="ri-layout-3-line text-white text-[10px]"></i>
          </div>
          عرض الصفحة
        </h3>
        <p className="text-xs text-gray-400 mb-4">حدد مدى اتساع محتوى الموقع</p>
        <div className="grid grid-cols-3 gap-3">
          {LAYOUT_OPTIONS.map(opt => (
            <button
              key={opt.id}
              onClick={() => updateDesign({ layoutId: opt.id })}
              className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all cursor-pointer ${
                design.layoutId === opt.id ? 'border-[#1a1a1a] bg-white' : 'border-gray-100 bg-white hover:border-gray-300'
              }`}
            >
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-gray-50">
                <i className={`${opt.icon} text-xl text-[#1a1a1a]`}></i>
              </div>
              <span className="text-xs font-bold text-[#1a1a1a]">{opt.label}</span>
              <span className="text-[10px] text-gray-400 text-center">{opt.desc}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Product Grid Columns */}
      <div className="bg-gray-50 rounded-2xl p-5 border border-gray-100">
        <h3 className="text-sm font-bold text-[#1a1a1a] mb-1 flex items-center gap-2">
          <div className="w-6 h-6 flex items-center justify-center bg-amber-400 rounded-lg">
            <i className="ri-grid-line text-black text-[10px]"></i>
          </div>
          أعمدة المنتجات
        </h3>
        <p className="text-xs text-gray-400 mb-4">عدد المنتجات في كل صف</p>
        <div className="flex items-center gap-4">
          <button
            onClick={() => updateDesign({ productGridCols: Math.max(2, design.productGridCols - 1) })}
            className="w-10 h-10 flex items-center justify-center rounded-xl border border-gray-200 bg-white hover:bg-gray-50 text-[#1a1a1a] font-bold cursor-pointer"
          >
            <i className="ri-subtract-line"></i>
          </button>
          <div className="flex-1 flex flex-col items-center gap-1">
            <span className="text-3xl font-black text-[#1a1a1a]">{design.productGridCols}</span>
            <span className="text-xs text-gray-400">أعمدة</span>
          </div>
          <button
            onClick={() => updateDesign({ productGridCols: Math.min(6, design.productGridCols + 1) })}
            className="w-10 h-10 flex items-center justify-center rounded-xl border border-gray-200 bg-white hover:bg-gray-50 text-[#1a1a1a] font-bold cursor-pointer"
          >
            <i className="ri-add-line"></i>
          </button>
        </div>
        <div className="mt-4 grid gap-2" style={{ gridTemplateColumns: `repeat(${design.productGridCols}, 1fr)` }}>
          {Array.from({ length: design.productGridCols }).map((_, i) => (
            <div key={i} className="h-12 rounded-lg" style={{ background: `linear-gradient(135deg, ${selectedColor.primaryLight}60, ${selectedColor.primary}40)` }}></div>
          ))}
        </div>
        <SaveButton state={layoutSaveState} onClick={onSaveLayout} label="حفظ التخطيط" />
      </div>

      {/* Hero Style */}
      <div className="bg-gray-50 rounded-2xl p-5 border border-gray-100">
        <h3 className="text-sm font-bold text-[#1a1a1a] mb-1 flex items-center gap-2">
          <div className="w-6 h-6 flex items-center justify-center bg-[#1a1a1a] rounded-lg">
            <i className="ri-image-2-line text-white text-[10px]"></i>
          </div>
          تصميم قسم الهيرو
        </h3>
        <p className="text-xs text-gray-400 mb-4">شكل القسم الرئيسي في أعلى الصفحة</p>
        <div className="grid grid-cols-2 gap-3">
          {HERO_STYLES.map(style => (
            <button
              key={style.id}
              onClick={() => updateDesign({ heroStyleId: style.id })}
              className={`flex items-center gap-3 p-3 rounded-xl border-2 transition-all cursor-pointer text-right ${
                design.heroStyleId === style.id ? 'border-[#1a1a1a] bg-white' : 'border-gray-100 bg-white hover:border-gray-300'
              }`}
            >
              <div className={`w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0 ${design.heroStyleId === style.id ? 'bg-[#1a1a1a]' : 'bg-gray-100'}`}>
                <i className={`${style.icon} text-sm ${design.heroStyleId === style.id ? 'text-white' : 'text-gray-500'}`}></i>
              </div>
              <span className="text-xs font-bold text-[#1a1a1a]">{style.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
